// Business information types
export interface Business {
  id: string;
  name: string;
  type: string;
  address: string;
  size: number;
  yearBuilt: number;
  operationalHours: {
    start: string;
    end: string;
    daysOpen: number[];
  };
  renewablePercentage: number;
  planType: string;
  contactName: string;
  contactEmail: string;
  zonesCount: number;
  badges: string[];
  joinDate: string;
}

// Building score types
export interface BuildingScore {
  overallScore: number;
  energyEfficiency: number;
  sustainabilityPerformance: number;
  historicalScores: {
    month: string;
    score: number;
  }[];
}

// Energy usage types
export interface MonthlyUsage {
  month: string;
  kWh: number;
  cost: number;
}

export interface ZoneUsage {
  zone: string;
  percentage: number;
  kWh: number;
}

export interface HourlyUsage {
  hour: string;
  kWh: number;
  operational: boolean;
}

export interface PeakDemand {
  day: string;
  time: string;
  kW: number;
}

export interface Anomaly {
  date: string;
  percentage: number;
  reason: string;
}

export interface ExpensiveUseTime {
  time: string;
  rate: string;
  cost: number;
}

export interface BillForecast {
  currentBill: number;
  forecastNextMonth: number;
  bestCase: number;
  worstCase: number;
}

export interface OperationalUsage {
  operational: number;
  nonOperational: number;
}

export interface EnergyUsageData {
  monthly: MonthlyUsage[];
  submetering: ZoneUsage[];
  hourly: HourlyUsage[];
  peakDemandTimes: PeakDemand[];
  anomalies: Anomaly[];
  expensiveUseTimes: ExpensiveUseTime[];
  billForecast?: BillForecast;
  operationalVsNonOperational: OperationalUsage;
}

// Sector comparison types
export interface SectorStats {
  averageScore: number;
  averageEnergyUsage: number;
  averageRenewable: number;
  topPerformer: {
    name: string;
    id: string;
    score: number;
  };
  businesses: {
    name: string;
    id: string;
    score: number;
  }[];
}

// Badge types
export interface Badge {
  id?: string;
  title: string;
  description: string;
  icon: string;
  criteria: string;
  shareText: string;
  color: string;
}

// Carbon impact types
export interface CarbonImpactData {
  totalCO2Avoided: number;
  treesEquivalent: number;
  carMilesEquivalent: number;
  coalSaved: number;
  smartphonesCharged: number;
  yearlyData: {
    month: string;
    co2Avoided: number;
    yearMonth: string;
  }[];
  period?: {
    start: string;
    end: string;
  };
}

// Energy overview types
export interface EnergyOverviewData {
  lastMonthKwh: number;
  lastMonthName: string;
  thisMonthSoFarKwh: number;
  thisMonthName: string;
  monthlyAvgKwh: number;
  peakUsageTimes: {
    date: string;
    formattedDate: string;
    day: string;
    time: string;
    kwh: number;
  }[];
  periodCovered: {
    start: string;
    end: string;
  };
}

// Energy insights types
export interface TimeSeriesDataPoint {
  timestamp: string;
  kwh: number;
}

export interface YearlyDataPoint {
  month: string;
  year: number;
  kwh: number;
}

// Legacy types for backward compatibility
export interface DailyUsageData {
  time: string;
  kwh: number;
  hour: number;
  isOperational: boolean;
}

export interface WeeklyUsageData {
  time: string;
  day: string;
  hour: number;
  kwh: number;
  isOperational: boolean;
}

export interface MonthlyUsageData {
  week: string;
  weekNumber: number;
  kwh: number;
  isOperational: boolean;
}

export interface YearlyUsageData {
  month: string;
  monthNumber: number;
  kwh: number;
  previousYear: number;
  isOperational: boolean;
}

export interface PeakTimeData {
  date: string;
  formattedDate: string;
  day: string;
  time: string;
  kwh: number;
}

export interface OperationalUsageData {
  month: string;
  operational: number;
  nonOperational: number;
}

export interface HourlyAvgData {
  hour: number;
  time: string;
  kwh: number;
  isOperational: boolean;
}

export interface PeriodCoveredData {
  start: string;
  end: string;
  latestDay: string;
  latestWeek: {
    start: string;
    end: string;
  };
  latestMonth: string;
  latestYear: string;
}

export interface EnergyInsightsData {
  // New data format
  daily_latest: TimeSeriesDataPoint[];
  weekly_latest_hourly: TimeSeriesDataPoint[];
  monthly_latest_daily: TimeSeriesDataPoint[];
  yearly_latest_monthly: {
    current_year: YearlyDataPoint[];
    previous_year: YearlyDataPoint[];
  };
  hourly_last_7_days: TimeSeriesDataPoint[];
  all_time_monthly: YearlyDataPoint[];
  
  // Legacy format for backward compatibility
  usagePatterns?: {
    daily: DailyUsageData[];
    weekly: WeeklyUsageData[];
    monthly: MonthlyUsageData[];
    yearly: YearlyUsageData[];
  };
  
  peakTimesLatestMonth: PeakTimeData[];
  operationalUsage: OperationalUsageData;
  dayTypePatterns: {
    weekday: HourlyAvgData[];
    weekend: HourlyAvgData[];
  };
  periodCovered: PeriodCoveredData;
}

export interface UsageForecastData {
  forecastSeries: {
    month: string;
    monthName: string;
    actualKwh: number | null;
    forecastKwh: number | null;
  }[];
  forecast_1_month: {
    actual: {
      date: string;
      kwh: number;
    }[];
    forecast: {
      date: string;
      kwh: number;
    }[];
  };
  forecast_3_months: {
    actual: {
      date: string;
      kwh: number;
    }[];
    forecast: {
      date: string;
      kwh: number;
    }[];
  };
  forecast_1_year: {
    actual: {
      date: string;
      kwh: number;
    }[];
    forecast: {
      date: string;
      kwh: number;
    }[];
  };
  weekly: {
    period: string;
    forecastKwh: number;
  };
  monthly: {
    period: string;
    forecastKwh: number;
  };
  yearly: {
    period: string;
    forecastKwh: number;
  };
  currentMonth: {
    month: string;
    name: string;
    actualKwh: number;
    forecastKwh: number | null;
  };
  periodCovered: {
    start: string;
    end: string;
  };
}

// Peak demand types
export interface PeakDemandData {
  last_30_days_top_peaks: {
    date: string;
    formatted_date: string;
    hour: number;
    formatted_hour: string;
    kwh: number;
  }[];
  monthly_analysis: {
    [yearMonth: string]: {
      year: number;
      month: number;
      month_name: string;
      formatted_month: string;
      top_10_absolute_peaks: {
        date: string;
        formatted_date: string;
        hour: number;
        formatted_hour: string;
        kwh: number;
      }[];
      average_peak_hour_overall: {
        hour: number;
        formatted_hour: string;
        avg_kwh: number;
      };
      average_peak_hour_by_weekday: {
        [day: string]: {
          hour: number | null;
          formatted_hour: string;
          avg_kwh: number;
        };
      };
    };
  };
  period_covered: {
    start: string;
    end: string;
    last_30_days_start: string;
  };
}

// Type for badge definitions
interface BadgeDefinitionsType {
  [key: string]: Badge;
}

// Type for business data
interface BusinessesData {
  [key: string]: Business;
}

// Add badges data directly in the file
const badgeDefinitionsData: BadgeDefinitionsType = {
  '100_renewable': {
    title: '100% Renewable Leader',
    description: 'Business operates on 100% renewable energy',
    icon: '🌱',
    criteria: 'Maintain 100% renewable energy for 3 or more consecutive months',
    shareText: 'We\'re proud to be powered by 100% renewable energy!',
    color: '#0CAA6E'
  },
  'energy_efficient': {
    title: 'Energy Efficiency Champion',
    description: 'Top-tier energy efficiency score',
    icon: '⚡',
    criteria: 'Achieve an energy efficiency score of 85 or higher',
    shareText: 'We\'ve achieved top-tier energy efficiency in our operations!',
    color: '#3B82F6'
  },
  'community_leader': {
    title: 'Community Sustainability Leader',
    description: 'Leading sustainability practices in the community',
    icon: '🏆',
    criteria: 'Be in the top 10% of sustainable businesses in your sector',
    shareText: 'We\'re leading our community in sustainable business practices!',
    color: '#8B5CF6'
  },
  'carbon_reducer': {
    title: 'Carbon Reduction Champion',
    description: 'Significant reduction in carbon footprint',
    icon: '🌍',
    criteria: 'Reduce carbon emissions by 20% or more year-over-year',
    shareText: 'We\'ve significantly reduced our carbon footprint!',
    color: '#0A5F3C'
  },
  'innovation_leader': {
    title: 'Green Innovation Leader',
    description: 'Innovative approaches to sustainability',
    icon: '💡',
    criteria: 'Implement innovative energy-saving technologies or practices',
    shareText: 'We\'re using innovative approaches to create a sustainable future!',
    color: '#6366F1'
  },
  'energy_saver': {
    title: 'Energy Saver',
    description: 'Consistent reduction in energy consumption',
    icon: '📉',
    criteria: 'Reduce energy consumption by 15% or more year-over-year',
    shareText: 'We\'re committed to reducing our energy consumption!',
    color: '#F59E0B'
  }
};

// Green Earth Cafe data
const businessData: BusinessesData = {
  BIZ001: {
    id: 'BIZ001',
    name: 'Green Earth Cafe',
    type: 'Restaurant/Cafe',
    address: '123 Main St, Irvine, CA 92617',
    size: 2500, // sq ft
    yearBuilt: 2008,
    operationalHours: {
      start: '7:00 AM',
      end: '9:00 PM',
      daysOpen: [1, 2, 3, 4, 5, 6, 0], // 0 is Sunday
    },
    renewablePercentage: 100, // 100% renewable energy
    planType: 'renewable',
    contactName: 'Emma Johnson',
    contactEmail: 'emma@greenearthcafe.com',
    zonesCount: 4, // kitchen, dining, storage, office
    badges: ['100_renewable', 'energy_efficient', 'community_leader', 'carbon_reducer'],
    joinDate: '2022-03-15'
  }
};

// Function implementations
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const setToken = (businessId: string) => {
  localStorage.setItem('businessId', businessId);
};

export const getBusinessId = (): string | null => {
  return localStorage.getItem('businessId');
};

export const clearToken = () => {
  localStorage.removeItem('businessId');
};

export const isAuthenticated = (): boolean => {
  return !!getBusinessId();
};

export const login = async (businessId: string): Promise<string> => {
  // Only allow BIZ001 to login
  if (businessId !== 'BIZ001') {
    throw new Error('Invalid business ID. Only Green Earth Cafe (BIZ001) is supported.');
  }
  
  // Simulate network delay for login
  await delay(600);
  
  // Set token in localStorage
  setToken(businessId);
  
  return businessId;
};

export const getBusiness = async (businessId: string): Promise<Business> => {
  // Only return data for BIZ001
  if (businessId !== 'BIZ001') {
    throw new Error('Only Green Earth Cafe (BIZ001) is supported.');
  }
  
  // Simulate network delay
  await delay(300);
  
  return businessData[businessId];
};

export const getBuildingScore = async (businessId: string): Promise<BuildingScore | null> => {
  // We're removing this feature as per instructions
  return null;
};

export const getEnergyUsage = async (businessId: string): Promise<EnergyUsageData | null> => {
  if (businessId !== 'BIZ001') {
    throw new Error('Only Green Earth Cafe (BIZ001) is supported.');
  }
  
  // This is a beta feature, return null
  return null;
};

export const getSectorData = async (businessType: string): Promise<SectorStats | null> => {
  // We're removing this feature as per instructions
  return null;
};

export const getLeaderboard = async (category: string): Promise<any[]> => {
  // We're removing this feature as per instructions
  return [];
};

export const getBadges = async (businessId: string): Promise<Badge[]> => {
  if (businessId !== 'BIZ001') {
    throw new Error('Only Green Earth Cafe (BIZ001) is supported.');
  }
  
  // Simulate network delay
  await delay(300);
  
  // Get business data
  const business = await getBusiness(businessId);
  
  // Map badge IDs to full badge objects
  return business.badges.map(badgeId => {
    const badge = badgeDefinitionsData[badgeId];
    return {
      ...badge,
      id: badgeId
    };
  });
};

export const getBadgeById = async (badgeId: string): Promise<Badge> => {
  // Simulate network delay
  await delay(200);
  
  const badge = badgeDefinitionsData[badgeId];
  
  if (!badge) {
    throw new Error(`Badge with ID ${badgeId} not found.`);
  }
  
  return {
    ...badge,
    id: badgeId
  };
};

export const getAllBadges = async (): Promise<Record<string, Badge>> => {
  // Simulate network delay
  await delay(300);
  
  // Convert to Record with ids
  const badgesWithIds: Record<string, Badge> = {};
  
  Object.entries(badgeDefinitionsData).forEach(([id, badge]) => {
    badgesWithIds[id] = {
      ...badge,
      id
    };
  });
  
  return badgesWithIds;
};

export const getCarbonImpact = async (businessId: string): Promise<CarbonImpactData> => {
  if (businessId !== 'BIZ001') {
    throw new Error('Only Green Earth Cafe (BIZ001) is supported.');
  }
  
  try {
    const response = await fetch('/data/carbon_impact_BIZ001.json');
    if (!response.ok) {
      throw new Error('Failed to fetch carbon impact data');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching carbon impact data:', error);
    throw error;
  }
};

export const getEnergyOverview = async (businessId: string): Promise<EnergyOverviewData> => {
  if (businessId !== 'BIZ001') {
    throw new Error('Only Green Earth Cafe (BIZ001) is supported.');
  }
  
  try {
    const response = await fetch('/data/energy_overview_BIZ001.json');
    if (!response.ok) {
      throw new Error('Failed to fetch energy overview data');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching energy overview data:', error);
    throw error;
  }
};

export const getEnergyInsights = async (businessId: string): Promise<EnergyInsightsData> => {
  if (businessId !== 'BIZ001') {
    throw new Error('Only Green Earth Cafe (BIZ001) is supported.');
  }
  
  try {
    const response = await fetch('/data/energy_insights_BIZ001.json');
    if (!response.ok) {
      throw new Error('Failed to fetch energy insights data');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching energy insights data:', error);
    throw error;
  }
};

export const getUsageForecast = async (businessId: string): Promise<UsageForecastData> => {
  if (businessId !== 'BIZ001') {
    throw new Error('Only Green Earth Cafe (BIZ001) is supported.');
  }
  
  try {
    const response = await fetch('/data/usage_forecast_BIZ001.json');
    if (!response.ok) {
      throw new Error('Failed to fetch usage forecast data');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching usage forecast data:', error);
    throw error;
  }
};

export const getSeasonalInsights = async (businessId: string) => {
  if (businessId !== 'BIZ001') {
    throw new Error('Only Green Earth Cafe (BIZ001) is supported.');
  }
  
  try {
    const response = await fetch('/data/seasonal_insights_BIZ001.json');
    if (!response.ok) {
      throw new Error('Failed to fetch seasonal insights data');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching seasonal insights data:', error);
    throw error;
  }
};

export const getEfficiencyMetrics = async (businessId: string) => {
  if (businessId !== 'BIZ001') {
    throw new Error('Only Green Earth Cafe (BIZ001) is supported.');
  }
  
  try {
    const response = await fetch('/data/efficiency_metrics_BIZ001.json');
    if (!response.ok) {
      throw new Error('Failed to fetch efficiency metrics data');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching efficiency metrics data:', error);
    throw error;
  }
};

export const getPeakDemand = async (businessId: string): Promise<PeakDemandData> => {
  if (businessId !== 'BIZ001') {
    throw new Error('Only Green Earth Cafe (BIZ001) is supported.');
  }
  
  try {
    const response = await fetch('/data/peak_demand_BIZ001.json');
    if (!response.ok) {
      throw new Error('Failed to fetch peak demand data');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching peak demand data:', error);
    throw error;
  }
}; 